import { PageTypes } from './pageTypes';
export const SIRONA_BROADCAST_CHANNEL = 'srna-event-system';

/**
 * `local` events will be transmitted in the same window through mitt.
 * `remote` events will use the BroadcastChannel (through the broadcast-channel library).
 */
export type Source = 'local' | 'remote';

export type DictationMessage =
  | {
      type: 'hypothesisText';
      text: string;
    }
  | {
      type: 'stableText';
      text: string;
    };

export interface FocusWindow {
  pageType: PageTypes;
  viewerId?: string;
}

export type ReporterShortcut =
  | {
      type: 'keyPress';
      action:
        | 'toggleRecording'
        | 'toggleCommandMode'
        | 'nextBracket'
        | 'previousBracket'
        | 'submitReport'
        | 'generateImpression'
        | 'discardReport'
        | 'draftReport';
    }
  | {
      type: 'gamepad';
      action: string;
      pressed: boolean;
      keyCode?: string;
    };

export type Dre<R> =
  | {
      type: 'rotate';
      amount: number;
    }
  | {
      type: 'flip';
      axis: 'horizontal' | 'vertical';
    }
  | {
      type: 'next_series_set';
    }
  | {
      type: 'prev_series_set';
    }
  | {
      type: 'reset';
    }
  | {
      type: 'reset-slice';
      setting: string;
    }
  | {
      type: 'expand';
      viewportId: string;
    }
  | {
      type: 'switch_viewtype';
    }
  | {
      type: 'SDKKeyboardShortcut';
      toolId: string;
      interactionId: string;
    }
  | {
      type: 'SDKTriggerToolInteractionResponse';
      responses: R;
    };

export interface ShowCrossTabNotification<R> {
  type: 'showCrossTabNotification';
  payload: R & {
    targetPageTypes: PageTypes[];
    // we can't send functions across tabs so we forbid them from being set to avoid confusion
    actionHandler?: never;
    onClose?: never;
    action?: never;
  };
}

export interface HideCrossTabNotification {
  type: 'closeCrossTabNotification';
  payload: {
    id: string;
  };
}

export enum CrossWindowDataRefetchType {
  CLAIMED_ITEMS = 'claimedItems',
  WORKLIST_ITEM_GROUP = 'worklistItemGroup',
  USER = 'user',
  WORKLIST = 'worklist',
  TOOL_PREFERENCES = 'toolPreferences',
  CASE = 'case',
}

export interface CrossWindowDataRefetch {
  type: CrossWindowDataRefetchType;
  skipLocalWorklistRefetch?: boolean;
}

export interface WorklistItemPatchPayload {
  type: 'worklistItemPatch';
  targetPage: PageTypes;
  data: {
    id: string;
    patch: Record<string, unknown>; // these are the fields to be patched on the worklist item
  };
}

export interface UserSettingsPatchPayload {
  type: 'userSettingsPatch';
  data: {
    id: string;
    patch: Record<string, unknown>; // these are the fields to be patched on the user settings
  };
}

export type CrossTabNotification<R> = ShowCrossTabNotification<R> | HideCrossTabNotification;

export interface ActivePicklistData<R> {
  type: 'activePicklistData';
  data: {
    activePicklist: R | null | undefined;
    activePicklistOptionID: string;
  };
}

export interface InputReportEventData {
  value: number;
  pressed: boolean;
}

export type DeepLink<R> = { target: PageTypes.VIEWER } & R;

export type DeepLinkPayloads<R> =
  | {
      type: 'INSERT';
      payload: DeepLink<R>;
    }
  | {
      type: 'HANDLE_IMAGE';
      payload: DeepLink<R>;
    }
  | {
      type: 'HANDLE_STUDY';
      payload: DeepLink<R>;
    };

export type RecorderVoiceCommandProps<R> = Readonly<
  R & {
    type: 'RecorderVoiceCommand';
  }
>;

export type CommandModeAudioPayload = {
  text?: string;
  audio: {
    name: string;
    mime_type: string;
    base64_data: string;
  };
  model_id?: string;
  prompt_id?: string;
  feature_name?: string;
};

export type CommandModeKeyPayload = {
  pressed: boolean;
};

export interface CancelReportCountdownPayload {
  worklistItemSmid: string;
}

export interface WebHidNotification<R> {
  message: string;
  severity: R;
}

export type ReportFacadeEnvelope = {
  type: string;
  id: string;
  ts: string;
  corr?: string;
  actor?: {
    kind: 'human' | 'agent' | 'llm';
    name?: string;
  };
  payload: unknown;
};

export type ReportFacadeBusPayload = {
  channel: string;
  env: ReportFacadeEnvelope;
};

/**
 * Payloads that can be sent through the cross-tab event system.
 * These payloads are used in the `useCrossTabEventsListener` hook and the `sendCrossTabEvent` function.
 *
 * Use the <R> generic to specify a complex refinement type for the payload.
 *
 * Example:
 * ```typescript
 *
 * export const useRecorderVoiceCommandEventsListener = (
 *   onEvent: ({
 *     source,
 *     payload,
 *   }: {
 *     source: Source;
 *     payload: RecorderVoiceCommandPropsWrapper<RecorderVoiceCommandProps>;
 *   }) => void
 * ) => useCrossTabEventsListener<RecorderVoiceCommandProps>(NameSpaces.RECORDER_VOICE_COMMAND, onEvent);
 *
 * ```
 */
export type Payload<R> =
  | RecorderVoiceCommandProps<R>
  | WebHidNotification<R>
  | ActivePicklistData<R>
  | DeepLinkPayloads<R>
  | Dre<R>
  | DictationMessage
  | ReporterShortcut
  | CrossTabNotification<R>
  | CrossWindowDataRefetch
  | InputReportEventData
  | CancelReportCountdownPayload
  | CommandModeAudioPayload
  | CommandModeKeyPayload
  | FocusWindow
  | ReportFacadeBusPayload
  | WorklistItemPatchPayload
  | UserSettingsPatchPayload
  | boolean
  | string;
